import { LoadingSpinner } from "components/LoadingSpinner";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import {
  collapseToRanges,
  prepareCodesForSubmit,
  RangeTooLargeError,
} from "helpers/codeRanges";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  useCreateUnitCapMutation,
  useGetUnitCapByIdQuery,
} from "store/queries/unitCap";

import {
  IUnitCapFormData,
  INITIAL_UNIT_CAP_FORM_DATA,
  UnitCapForm,
} from "./components/UnitCapForm";

export function CopyUnitCapPage() {
  useDocumentTitle("Copy Unit Cap Rule");
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const asDefault = searchParams.get("asDefault") === "true";
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const { data, isLoading: isFetching } = useGetUnitCapByIdQuery(id ?? "");
  const [createMutation, { isLoading: isCreating }] =
    useCreateUnitCapMutation();
  const [formData, setFormData] = useState<IUnitCapFormData>(
    INITIAL_UNIT_CAP_FORM_DATA
  );

  // A copy is a new scoped rule by default; scope is dropped unless the caller explicitly
  // asks to copy as the default via ?asDefault=true.
  useEffect(() => {
    const ruleset = data?.data;
    if (!ruleset) return;
    setFormData({
      maxUnitsPerCode: ruleset.maxUnitsPerCode,
      providerId: asDefault ? null : ruleset.providerId,
      payerId: asDefault ? null : ruleset.payerId,
      cptCodes: collapseToRanges(ruleset.cptCodes ?? []),
      groupName: ruleset.groupName ?? "",
      contractLanguage: ruleset.contractLanguage ?? "",
      isDefault: asDefault,
    });
  }, [data, asDefault]);

  const handleSubmit = async () => {
    if (!formData.isDefault && (!formData.providerId || !formData.payerId))
      return;
    let cptCodes: string[];
    try {
      cptCodes = prepareCodesForSubmit(formData.cptCodes);
    } catch (err) {
      toastElement?.show({
        severity: "error",
        summary: "Range too large",
        detail:
          err instanceof RangeTooLargeError
            ? err.message
            : "Failed to expand code ranges",
      });
      return;
    }
    setIsLoading(true);
    try {
      await createMutation({
        maxUnitsPerCode: formData.maxUnitsPerCode,
        cptCodes,
        groupName: formData.groupName.trim() || null,
        contractLanguage: formData.contractLanguage.trim() || null,
        // CLE-2524: applicability wins over the legacy isDefault flag.
        applicability: formData.isDefault ? "TYPICAL" : "SCOPED",
        isDefault: formData.isDefault,
        providerId: formData.isDefault ? null : formData.providerId,
        payerId: formData.isDefault ? null : formData.payerId,
      }).unwrap();
      toastElement?.show({
        severity: "success",
        summary: "Created!",
        detail: "Unit Cap rule copied successfully.",
      });
      navigate("/payer-rules");
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: error?.data?.message || "Failed to copy unit cap rule",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) return <LoadingSpinner />;

  return (
    <UnitCapForm
      title="Copy Unit Cap Rule"
      submitLabel="Create Copy"
      formData={formData}
      onChange={setFormData}
      onSubmit={handleSubmit}
      isSubmitting={isCreating}
    />
  );
}
